/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import type { GridConfig } from '@coursera-web/cds-labs';

import { isRightToLeft } from 'js/lib/language';

import { Button, Typography2, breakpoints, useMediaQuery } from '@coursera/cds-core';
import type { ButtonProps } from '@coursera/cds-core';
import { ArrowNextIcon } from '@coursera/cds-icons';

import { BasicCard, ShowMoreGridSection } from 'bundles/cds-labs/components';
import ModuleLayout, { noImage } from 'bundles/cds-labs/components/Modules/ModuleLayout';
import type { ModuleLayoutConfig } from 'bundles/cds-labs/components/Modules/ModuleLayout';
import Eyebrow from 'bundles/cds-labs/components/Modules/components/Eyebrow';
import Image, { type ImageType } from 'bundles/cds-labs/components/Modules/components/Image';
import PreservedUtmLink from 'bundles/cds-labs/components/Modules/components/PreservedUtmLink';
import { getColorFromColorName, isInvertedColor } from 'bundles/cds-labs/components/Modules/utils/colors';

import _t from 'i18n!nls/cds-labs';

const CARD_SIZE = { SMALL: 'small', REGULAR: 'regular', LARGE: 'large' } as const;

export type CardSize = (typeof CARD_SIZE)[keyof typeof CARD_SIZE];
export type ColorChoice = 'blue' | 'white' | 'black' | 'light-blue' | 'dark-blue' | 'grey';

type VariantType = 'grid' | 'list';

export type VariantConfig = {
  sm?: VariantType;
};

// The offset is half of the width of the card
const BACKGROUND_IMAGE_LEFT_OFFSET = 335;

export type CannedCollectionModuleCard = {
  id: string;
  headline?: string;
  body?: string;
  target?: string;
  ctaText?: string;
  cardBackgroundImage?: {
    width?: number;
    height?: number;
    url?: string;
    title?: string;
    description?: string;
  };
  eyebrow?: string;
  eyebrowBackground?: string;
  eyebrowImage?: ImageType;
  backgroundColor?: ColorChoice;
  onClick?: () => void;
  ctaVariant?: ButtonProps['variant'];
};

type Props = {
  heading?: string;
  subheading?: string;
  cards: CannedCollectionModuleCard[];
  size: CardSize;
  color: ColorChoice;
  preloadImage?: boolean;
  lazyLoadImage?: boolean;
  showAllEntries?: boolean;
  showImageInFull?: boolean;
  variantConfig?: VariantConfig;
} & Pick<ModuleLayoutConfig, 'backgroundColor' | 'removeTopPadding' | 'removeBottomPadding'>;

const styles = {
  card: css`
    display: block;
    width: 100%;
    height: 100%;
    border-radius: var(--cds-spacing-200);
    text-decoration: none;
    overflow: hidden;

    &:active,
    &:hover,
    &:focus {
      text-decoration: none;
    }
  `,

  largeCardMd: css`
    ${breakpoints.up('md')} {
      border-radius: var(--cds-spacing-300);
    }
  `,

  cardSizeLarge: css`
    min-height: 266px;
    padding: var(--cds-spacing-600) var(--cds-spacing-300);
    ${breakpoints.down('sm')} {
      padding: var(--cds-spacing-300);
    }
  `,

  cardSizeRegular: css`
    min-height: 192px;
    padding: var(--cds-spacing-300);
  `,

  cardSizeSmall: css`
    min-height: 150px;
    padding: var(--cds-spacing-200);
  `,

  cardColorBlack: css`
    background-color: var(--cds-color-neutral-primary);
    border: none;
  `,

  cardColorBlue: css`
    background-color: var(--cds-color-emphasis-primary-background-xweak);
    border: none;
  `,

  cardColorWhite: css`
    background-color: var(--cds-color-neutral-background-primary);
    border: 1px solid var(--cds-color-neutral-disabled-strong);
  `,

  cardTitleLarge: css`
    margin-bottom: var(--cds-spacing-50);
  `,

  cardTitle: css`
    margin-bottom: 2px;
  `,

  eyebrowSpacing: css`
    margin-bottom: var(--cds-spacing-150);
  `,

  cta: css`
    display: flex;
    align-items: center;
    margin-top: var(--cds-spacing-150);
  `,
  cardContentWrapper: css`
    position: relative;
    width: 100%;
    height: 100%;
  `,
  cardContent: (setMaxWidth: boolean) => css`
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    max-width: ${setMaxWidth ? `${BACKGROUND_IMAGE_LEFT_OFFSET}px` : 'unset'};
    box-sizing: border-box;
  `,
  backgroundImage: css`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    overflow: hidden;
  `,
  backgroundImageInner: (offset: number, isRtl: boolean) => css`
    position: absolute;
    bottom: 0;
    left: ${offset}px;

    img {
      ${isRtl ? `transform: scaleX(-1);` : ''};
    }
  `,

  // cancel the y-padding since the block already provides y-padding
  showMoreGridSection: css`
    padding-top: 0;
    padding-bottom: 0;
  `,

  subheading: css`
    margin-top: var(--cds-spacing-50);
  `,

  headingContainer: css`
    margin-bottom: var(--cds-spacing-150);
  `,

  backgroundImageContainer: css`
    position: absolute;
    top: 0;
    left: 100%;
    width: 100%;
    height: 100%;

    ${breakpoints.down('sm')} {
      position: relative;
      left: 0;
    }
  `,
  backgroundImageFull: css`
    display: flex;
    justify-content: flex-end;
    height: 100%;
  `,
  cardContentFull: css`
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    position: relative;
    z-index: 1;
    box-sizing: border-box;
    width: 50%; /* When background image is displayed in full, reserve half of card for image */

    ${breakpoints.down('sm')} {
      width: 100%;
    }
  `,
};

export function CannedCollectionModule(props: Props) {
  const {
    heading,
    subheading,
    cards,
    size,
    color,
    preloadImage,
    lazyLoadImage,
    showAllEntries,
    showImageInFull = false,
    variantConfig,
  } = props;
  const isRtl: boolean = isRightToLeft(_t.getLocale());
  const isMobile: boolean = useMediaQuery(breakpoints.down('xs'));

  // If true, cards are displayed in grid view for all viewport widths
  const isGridVariant = variantConfig?.sm === 'grid' && size === CARD_SIZE.LARGE;

  let cardSizeStyle = styles.cardSizeRegular;
  let cardTitleVariant: React.ComponentProps<typeof Typography2>['variant'] = 'subtitleLarge';
  let cardSubtitleVariant: React.ComponentProps<typeof Typography2>['variant'] = 'bodySecondary';

  switch (size) {
    case CARD_SIZE.LARGE:
      cardSizeStyle = styles.cardSizeLarge;
      cardSubtitleVariant = 'bodyPrimary';
      cardTitleVariant = 'titleXSmall';
      break;
    case CARD_SIZE.REGULAR:
      cardSizeStyle = styles.cardSizeRegular;
      break;
    case CARD_SIZE.SMALL:
      cardTitleVariant = 'subtitleMedium';
      break;
    default:
      cardSizeStyle = styles.cardSizeSmall;
      break;
  }

  const cardColorStyle = (cardBackgroundColor?: ColorChoice) => {
    return `background-color:
    ${getColorFromColorName(cardBackgroundColor)};
  `;
  };

  const cardBorderStyle =
    color === 'white' ? `border: 1px solid var(--cds-color-neutral-disabled-strong);` : `border: none;`;

  const renderCardContent = (item: CannedCollectionModuleCard) => {
    const backgroundImage = item.cardBackgroundImage;
    const cardBackgroundColor = item.backgroundColor ?? color;
    const isDarkBackground = cardBackgroundColor ? isInvertedColor(cardBackgroundColor) : false;
    const shouldShowImageInFull = showImageInFull && size === CARD_SIZE.LARGE;

    const cardStyles = [
      styles.card,
      size === CARD_SIZE.LARGE && styles.largeCardMd,
      cardColorStyle(cardBackgroundColor),
      cardBorderStyle,
    ].filter(Boolean);

    const renderBackgroundImage = () => {
      if (!backgroundImage?.url) return null;

      if (shouldShowImageInFull) {
        return (
          <div css={styles.backgroundImageContainer}>
            <div css={styles.backgroundImageFull}>
              <Image
                {...backgroundImage}
                style={{
                  height: '100%',
                  objectFit: 'cover',
                }}
                shouldPreload={preloadImage}
                shouldLazyLoad={lazyLoadImage}
              />
            </div>
          </div>
        );
      }

      // Default behavior
      return (
        <div css={styles.backgroundImage}>
          <div
            css={styles.backgroundImageInner(
              backgroundImage?.width
                ? Math.max(BACKGROUND_IMAGE_LEFT_OFFSET * 2 - backgroundImage?.width, BACKGROUND_IMAGE_LEFT_OFFSET)
                : BACKGROUND_IMAGE_LEFT_OFFSET,
              isRtl
            )}
          >
            <Image
              {...backgroundImage}
              style={{
                maxWidth: 'unset',
              }}
              shouldPreload={preloadImage}
              shouldLazyLoad={lazyLoadImage}
            />
          </div>
        </div>
      );
    };

    const getContentStyles = () => {
      if (shouldShowImageInFull) {
        return [styles.cardContentFull, cardSizeStyle];
      }
      return [styles.cardContent(!!backgroundImage?.url), cardSizeStyle];
    };

    const contents = (
      <div css={styles.cardContentWrapper}>
        <div css={getContentStyles()}>
          <div>
            {(item.eyebrow || item.eyebrowImage) && (
              <div css={styles.eyebrowSpacing}>
                <Eyebrow text={item.eyebrow} background={item.eyebrowBackground} image={item.eyebrowImage} />
              </div>
            )}
            {item.headline && (
              <Typography2
                component="h3"
                variant={cardTitleVariant}
                css={size === CARD_SIZE.LARGE ? styles.cardTitleLarge : styles.cardTitle}
                color={isDarkBackground ? 'invertBody' : 'body'}
              >
                {item.headline}
              </Typography2>
            )}
            {item.body && (
              <Typography2
                variant={cardSubtitleVariant}
                component="div"
                color={isDarkBackground ? 'invertBody' : 'body'}
              >
                {item.body}
              </Typography2>
            )}
          </div>
          {item.ctaText && (
            <div>
              <Button
                css={styles.cta}
                size="small"
                variant={item.ctaVariant || (isDarkBackground ? 'secondary' : 'primary')}
                icon={
                  <ArrowNextIcon
                    color={isDarkBackground && !item.ctaVariant?.includes('ghost') ? 'interactive' : 'invert'}
                  />
                }
                iconPosition="after"
              >
                {item.ctaText}
              </Button>
            </div>
          )}
          {renderBackgroundImage()}
        </div>
      </div>
    );

    if (item.target) {
      const handleClick = () => {
        if (item.onClick) {
          item.onClick();
        }
      };
      return (
        <BasicCard
          component={PreservedUtmLink}
          href={item.target}
          openInNewTab={true}
          css={cardStyles}
          role="button"
          onClick={handleClick}
          trackingName="canned_collection_card"
        >
          {contents}
        </BasicCard>
      );
    } else {
      return <div css={cardStyles}>{contents}</div>;
    }
  };

  let numDisplayedRows = size === CARD_SIZE.SMALL ? 2 : 1;
  if (showAllEntries) {
    numDisplayedRows = cards.length;
  }

  const getItemsGridConfig = () => {
    if (isGridVariant) {
      return { xs: 6, sm: 6, md: 6 } as GridConfig;
    } else if (size === CARD_SIZE.LARGE) {
      return { xs: 12, sm: 12, md: 6 } as GridConfig;
    }

    return { xs: 12, sm: 6, md: 4 } as GridConfig;
  };

  const contentPanel = () => (
    <div>
      {(heading || subheading) && (
        <div css={styles.headingContainer}>
          {heading && (
            <Typography2 variant="titleSmall" component="h2" color="inherit">
              {heading}
            </Typography2>
          )}
          {subheading && (
            <Typography2 variant="subtitleMedium" component="h3" color="inherit" css={styles.subheading}>
              {subheading}
            </Typography2>
          )}
        </div>
      )}
      <ShowMoreGridSection
        items={cards}
        spacing={isMobile ? 8 : 16}
        itemsGridConfig={getItemsGridConfig()}
        defaultRows={numDisplayedRows}
        renderItem={({ item }) => renderCardContent(item)}
        css={styles.showMoreGridSection}
      />
    </div>
  );

  return <ModuleLayout assembly={noImage(props)} renderContent={contentPanel} />;
}

export default CannedCollectionModule;
